import { SuretyAccountStatus } from "@inrev/common";
import {
	useFetchAccountCompanies,
	useFetchAccountIndividuals,
	useFetchAccountLatestIndemnityAgreement,
	useFetchSuretyAccount,
} from "@portal/api/account";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { FC } from "react";
import { AccountTabProps } from "../../account/AccountLayout";
import { IndemnityAgreementSection } from "./indemnity/IndemnityAgreementSection";

type AccountIndemnityTabProps = AccountTabProps;

export const AccountIndemnityTab: FC<AccountIndemnityTabProps> = ({ accountId }) => {
	const { suretyAccount, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);
	const { companies, companiesError, companiesIsLoading, refetchAccountCompanies } =
		useFetchAccountCompanies(accountId);
	const { individuals, individualsError, individualsIsLoading, refetchAccountIndividuals } =
		useFetchAccountIndividuals(accountId);
	const {
		latestIndemnityAgreement,
		latestIndemnityAgreementError,
		latestIndemnityAgreementIsLoading,
		refetchLatestIndemnityAgreement,
	} = useFetchAccountLatestIndemnityAgreement(accountId);

	if (suretyAccountError) {
		return (
			<ReportedRetryableLoadError
				error={suretyAccountError}
				resource="account"
				onRetry={refetchSuretyAccount}
			/>
		);
	}

	if (companiesError) {
		return (
			<ReportedRetryableLoadError
				error={companiesError}
				resource="account companies"
				onRetry={refetchAccountCompanies}
			/>
		);
	}

	if (individualsError) {
		return (
			<ReportedRetryableLoadError
				error={individualsError}
				resource="account individuals"
				onRetry={refetchAccountIndividuals}
			/>
		);
	}

	if (latestIndemnityAgreementError) {
		return (
			<ReportedRetryableLoadError
				error={latestIndemnityAgreementError}
				resource="indemnity agreement"
				onRetry={refetchLatestIndemnityAgreement}
			/>
		);
	}

	if (
		companiesIsLoading ||
		individualsIsLoading ||
		latestIndemnityAgreementIsLoading ||
		!suretyAccount ||
		!companies ||
		!individuals
	) {
		return <CenteredSpinner />;
	}

	return (
		<div
			className="relative flex flex-col w-full pb-[125px]"
			data-testid="agent-account-indemnity-tab"
		>
			<IndemnityAgreementSection
				accountId={accountId}
				companies={companies}
				individuals={individuals}
				indemnityAgreement={latestIndemnityAgreement ?? undefined}
				isAccountClosed={suretyAccount.status === SuretyAccountStatus.closed}
			/>
		</div>
	);
};
